import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import 'bootstrap-daterangepicker/daterangepicker.css';
import moment from 'moment';
import { Spinner } from 'react-bootstrap';
import { FaHistory } from 'react-icons/fa';
import ReactReadMoreReadLess from "react-read-more-read-less";
import { components } from 'react-select';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';
import { KTSVG } from '../../../_metronic/helpers';
import { PageTitle } from '../../../_metronic/layout/core';
import CurrencyComp from '../../../components/currencyComponent/CurrencyComp';
import DateComp from '../../../components/dateComponent/DateComp';
import DateRangeComp from '../../../components/dateComponent/DateRangeComp';
import { LoadingComponent } from '../../../components/loadingComponent/LoadingComponent';
import NoRecordFound from '../../../components/noRecord/NoRecordFound';
import Pagination from '../../../components/pagination/Pagination';
import RefreshComponent from '../../../components/refreshComponent/RefreshComponent';
import TableHeading from '../../../components/tableHeading/TableHeading';
import TableSettingMenu from '../../../components/tableHeading/TableSettingMenu';
import SearchSelect from '../../../components/textFields/SearchSelect';
import TextField from '../../../components/textFields/TextField';
import { constraintConfig } from '../../../constraintConfig';
import { selectAuth } from '../../../features/authSlice';
import { useAppSelector } from '../../../hooks';
import { useLazyGetExpenseItemByExpenseIdQuery } from '../../../services/ExpenseApi';
import { useExpenseStatusLookupQuery, useGetAllExpenseTypeQuery, useGetAllUsersManagerAdminQuery, useGetReportExpenseStatusQuery } from '../../../services/GeneralApi';
import { useGetExpensesReportByUserV2Query } from '../../../services/ReportAndAnalyticsApi';
import useColumnVisibility from '../../customHooks/useColumnVisibility';
import labelKey from "../../localization/label.json";
import tosterKey from "../../localization/toster.json";
import ExpenseDetailModal from '../expenseItems/ExpenseDetailModal';
import ExpenseLogModal from '../expenseItems/ExpenseLogModal';
import DownloadReport from '../reportAnalytics/DownloadReport';
import ImpersonateUserModal from '../users/ImpersonateUserModal';
import DeleteExpenseModal from '../expenseItems/DeleteExpenseModal';
import ExpenseStatusComp from '../../../components/StatusComp/ExpenseStatusComp';
import SsRowActions from '../../../components/tableActions/SsRowActions';

const { Option } = components;
type Option = {
    value: string;
    label: string;
};

// Custom Option component to add checkboxes
const CheckboxOption = (props: any) => {
    return (
        <Option {...props} className='d-flex align-items-start gap-2'>
            <input
                type="checkbox"
                checked={props.isSelected}
                onChange={() => null}
            />{" "}
            <label>{props.label}</label>
        </Option>
    );
};

// Column configuration with proper typing
interface ColumnConfig {
    id: string;
    labelKey?: keyof typeof labelKey; // Made optional
    sortable: boolean;
    dataKey: string;
    customLabel: string; // Made required for all columns
}

const COLUMN_CONFIG: ColumnConfig[] = [
    { id: 'reportId', labelKey: 'reportId', sortable: false, dataKey: 'trackingId', customLabel: 'Report ID' },
    { id: 'user', labelKey: 'user', sortable: false, dataKey: 'userAccount', customLabel: 'User' },
    { id: 'title', labelKey: 'title', sortable: true, dataKey: 'title', customLabel: 'Title' },
    { id: 'expenseDetail', labelKey: 'expenseDetail', sortable: true, dataKey: 'expenseDetail', customLabel: 'Expense Detail' },
    { id: 'amount', labelKey: 'totalAmount', sortable: true, dataKey: 'amount', customLabel: 'Total Amount' },
    { id: 'createdDate', labelKey: 'date', sortable: true, dataKey: 'reportDate', customLabel: 'Date' },
    { id: 'expenseStatus', labelKey: 'status', sortable: true, dataKey: 'expenseStatus', customLabel: 'Status' },
    { id: 'manager', labelKey: 'assignTo', sortable: true, dataKey: 'manager', customLabel: 'Assign To' },
    { id: 'comment', labelKey: 'comment', sortable: false, dataKey: 'comment', customLabel: 'Comment' },
    { id: 'syncSSToBCDateTime', labelKey: 'syncToBCDate', sortable: true, dataKey: 'syncSSToBCDateTime', customLabel: 'Sync To BC Date' },
    { id: 'syncSSToBCStatusCode', labelKey: 'syncToBCStatus', sortable: false, dataKey: 'syncSSToBCStatusCode', customLabel: 'Sync To BC Status' },
    { id: 'syncToBCStatusMsg', labelKey: 'syncToBCStatusMsg', sortable: false, dataKey: 'syncToBCStatusMsg', customLabel: 'Sync To BC Status Msg' },
    { id: 'bcExpenseCreated', labelKey: 'bCExpenseCreated', sortable: true, dataKey: 'bcExpenseCreated', customLabel: 'BC Expense Created' },
    { id: 'bcExpenseCreatedDateTime', labelKey: 'bcExpenseCreatedDate', sortable: true, dataKey: 'bcExpenseCreatedDateTimeNew', customLabel: 'BC Expense Created Date' },
    { id: 'bcRespExpenseStatus', labelKey: 'bcRespExpenseStatus', sortable: true, dataKey: 'bcRespExpenseStatus', customLabel: 'BC Resp Expense Status' },
    { id: 'bcPurchasedInvoiceno', sortable: false, dataKey: 'bcPurchasedInvoiceno', customLabel: 'BC Purchased Invoice No' },
    { id: 'creditMemoNo', sortable: false, dataKey: 'creditMemoNo', customLabel: 'BC Credit Memo No' },
    { id: 'bcPostedPurchasedInvoiceNo', labelKey: 'bcPostedPurchasedInvoiceNo', sortable: false, dataKey: 'bcPostedPurchaseHeaderNo', customLabel: 'BC Posted Purchased Invoice No' },
    { id: 'bcPostedPurchaseInvoiceRemainingAmount', labelKey: 'bcPostedPurchaseInvoiceRemainingAmount', sortable: false, dataKey: 'bcPostedPurchaseInvoiceRemainingAmount', customLabel: 'BC Posted Purchase Invoice Remaining Amount' },
    { id: 'bcPostedPurchaseInvoiceClosed', labelKey: 'bcPostedPurchaseInvoiceClosed', sortable: false, dataKey: 'bcPostedPurchaseInvoiceClosed', customLabel: 'BC Posted Purchase Invoice Closed' },
    { id: 'bcTotalAmountPosted', labelKey: 'bcTotalAmountPosted', sortable: false, dataKey: 'bcTotalAmountPosted', customLabel: 'BC Total Amount Posted' },
    { id: 'bcTotalExpenseLineItemsCount', labelKey: 'bcTotalExpenseLineItemsCount', sortable: false, dataKey: 'bcTotalExpenseLineItemsCount', customLabel: 'BC Total Expense Line Items Count' },
    { id: 'bcTotalExpenseLineItemsSplitCount', labelKey: 'bcTotalExpenseLineItemsSplitCount', sortable: false, dataKey: 'bcTotalExpenseLineItemsSplitCount', customLabel: 'BC Total Expense Line Items Split Count' },
    { id: 'bcRespTotalAmountExcludingTax', labelKey: 'bcRespTotalAmountExcludingTax', sortable: false, dataKey: 'bcRespTotalAmountExcludingTax', customLabel: 'BC Resp Total Amount Excluding Tax' },
    { id: 'bcRespTotalAmountIncludingTax', labelKey: 'bcRespTotalAmountIncludingTax', sortable: false, dataKey: 'bcRespTotalAmountIncludingTax', customLabel: 'BC Resp Total Amount Including Tax' },
    { id: 'approvedDate', labelKey: 'apApprovedDate', sortable: false, dataKey: 'approvedDate', customLabel: 'AP Approved Date' },
    { id: 'paidDate', labelKey: 'paidDate', sortable: false, dataKey: 'paidDate', customLabel: 'Paid Date' },
    { id: 'lastBCtoSSUpdatedBy', labelKey: 'lastBCtoSSUpdatedBy', sortable: false, dataKey: 'lastBCtoSSUpdatedBy', customLabel: 'Last BC to SS Updated By' },
    { id: 'lastBCtoSSUpdatedDate', labelKey: 'lastBCtoSSUpdatedDate', sortable: false, dataKey: 'lastBCtoSSUpdatedDate', customLabel: 'Last BC to SS Updated Date' },
    { id: 'updatedByName', labelKey: 'lastUpdatedBy', sortable: false, dataKey: 'updatedByName', customLabel: 'Last Updated By' },
    { id: 'lastUpdatedDate', labelKey: 'lastUpdatedDate', sortable: false, dataKey: 'updatedDate', customLabel: 'Last Updated Date' },
    { id: 'actions', labelKey: 'actions', sortable: false, dataKey: 'actions', customLabel: 'Actions' },
];

// Helper function to get label text
const getLabelText = (col: ColumnConfig): string => {
    // If labelKey exists and has a value in labelKey, use it
    if (col.labelKey && labelKey[col.labelKey]) {
        return labelKey[col.labelKey];
    }
    // Otherwise use customLabel
    return col.customLabel;
};

// Render functions for different column types
const renderCell = (data: any, columnId: string, labelKey: any) => {
    switch (columnId) {
        case 'reportId':
            return data?.trackingId;

        case 'user':
            return data?.userAccount ? (
                <div className="d-flex align-items-center">
                    <div className="symbol symbol-circle symbol-50px overflow-hidden me-3">
                        <div className="symbol-label fs-3 bg-light-info text-info text-capitalize">
                            {data?.userAccount?.firstName?.charAt(0)}
                        </div>
                    </div>
                    <div className="d-flex flex-column">
                        <p className="text-gray-800 fw-bold text-hover-primary mb-1 text-capitalize">
                            {data?.userAccount?.firstName} {data?.userAccount?.middleName} {data?.userAccount?.lastName}
                        </p>
                        <span className='text-lowercase'>{data?.userAccount?.email}</span>
                    </div>
                </div>
            ) : null;

        case 'expenseDetail':
            const detail = data?.expenseDetail || '';
            return detail.length > 60 ? `${detail.substring(0, 40)}...` : detail;

        case 'amount':
            return <CurrencyComp amount={data?.amount} />;

        case 'createdDate':
            return <DateComp formattedDate={data.reportDate} />;

        case 'expenseStatus':
            return data?.expenseStatus ? (
                <ExpenseStatusComp 
                    expenseStatusId={data?.expenseStatus.id} 
                    expenseStatusTitle={data?.expenseStatus.title} 
                />
            ) : null;

        case 'manager':
            return data?.manager?.firstName ? (
                <div className='d-flex align-items-center'>
                    <div className='symbol symbol-40px symbol-circle me-3'>
                        {data?.manager?.imageUrl ? 
                            <img src={data?.manager?.imageUrl} alt="" /> :
                            <div className="symbol-label fs-3 bg-light-info text-info text-capitalize">
                                {data?.manager?.firstName?.charAt(0)}
                            </div>
                        }
                    </div>
                    <div className='d-flex justify-content-start flex-column'>
                        <p className='text-capitalize mb-0'>
                            {data?.manager?.firstName} {data?.manager?.middleName} {data?.manager?.lastName}
                        </p>
                        <span className='text-muted fw-semibold text-muted d-block fs-7 text-lowercase'>
                            {data?.manager?.email}
                        </span>
                    </div>
                </div>
            ) : null;

        case 'comment':
            return data?.comment ? (
                <ReactReadMoreReadLess
                    charLimit={30}
                    readMoreText={"Read more"}
                    readLessText={"Read less"}
                    readMoreClassName="readMore"
                    readLessClassName="readLess"
                >
                    {data.comment}
                </ReactReadMoreReadLess>
            ) : null;

        case 'syncSSToBCDateTime':
        case 'bcExpenseCreatedDateTime':
        case 'approvedDate':
        case 'paidDate':
        case 'lastBCtoSSUpdatedDate':
        case 'lastUpdatedDate':
            const dateValue = columnId === 'bcExpenseCreatedDateTime' 
                ? data?.bcExpenseCreatedDateTimeNew || data?.bcExpenseCreatedDateTime
                : columnId === 'approvedDate'
                ? data?.approvedDate || data?.approverDate
                : data?.updatedDate || data?.[columnId];
            return <DateComp formattedDate={dateValue} />;

        case 'bcExpenseCreated':
            return data?.bcExpenseCreated === true ? "Yes" : data?.bcExpenseCreated === false ? "No" : "";

        case 'bcTotalAmountPosted':
            const amountPosted = data?.bcTotalAmountPosted;
            return (
                <>
                    {amountPosted != null && (
                        <div className={Number(data?.amount) !== Number(amountPosted) ? 'text-danger' : ''}>
                            <CurrencyComp amount={amountPosted} />
                        </div>
                    )}
                    {amountPosted != null && Number(data?.amount) !== Number(amountPosted) && (
                        <span className='badge badge-light-danger fs-8 fw-semibold'>
                            {labelKey.invalidAmountPostedToBC}
                        </span>
                    )}
                </>
            );

        case 'bcTotalExpenseLineItemsCount':
        case 'bcTotalExpenseLineItemsSplitCount':
            return <span className='text-center'>{data?.[columnId] ?? ''}</span>;

        case 'bcRespTotalAmountExcludingTax':
        case 'bcRespTotalAmountIncludingTax':
            const taxAmount = data?.[columnId];
            return taxAmount != null ? <CurrencyComp amount={taxAmount} /> : '';

        default:
            const config = COLUMN_CONFIG.find((c) => c.id === columnId);
            return (config?.dataKey ? data?.[config.dataKey] : undefined) ?? data?.[columnId] ?? '';
    }
};

const ExpenseSearch = () => {
    const currentDate = moment().format('MM/DD/YYYY');
    const last30Days = moment().subtract(89, 'days').format('MM/DD/YYYY');
    const { hiddenColumns, handleToggleColumn } = useColumnVisibility({ pageName: 'searchExpense' });
    const { userAccountID, roleID, reportPermision } = useAppSelector(selectAuth);
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(10);
    const [searchReporID, setSearchReporID] = useState('');
    const [localSearchReportID, setLocalSearchReportID] = useState('');

    const [sortBy, setSortBy] = useState("createdDate");
    const [sortOrder, setSortOrder] = useState("desc");
    const [filterByStartDate, setFilterByStartDate] = useState(last30Days);
    const [filterByEndDate, setFilterByEndDate] = useState(currentDate);
    const [sortedColumn, setSortedColumn] = useState("createdDate");
    const [filterByUserIds, setFilterByUserIds] = useState("");
    const [filterByStatus, setFilterByStatus] = useState("0");
    const [filterByBCExpenseCreated, setFilterByBCExpenseCreated] = useState<string>("");
    const [filterByBCRespExpenseStatus, setFilterByBCRespExpenseStatus] = useState("");
    const [filterByBCCreatedStartDate, setFilterByBCCreatedStartDate] = useState("");
    const [filterByBCCreatedEndDate, setFilterByBCCreatedEndDate] = useState("");

    const [localFilterByStartDate, setLocalFilterByStartDate] = useState(last30Days);
    const [localFilterByEndDate, setLocalFilterByEndDate] = useState(currentDate);
    const [localFilterByUserIds, setLocalFilterByUserIds] = useState("");
    const [localFilterByStatus, setLocalFilterByStatus] = useState("0");
    const [localFilterByHideFromManager, setLocalFilterByHideFromManager] = useState("0");
    const [localFilterByBCExpenseCreated, setLocalFilterByBCExpenseCreated] = useState<string>("");
    const [localFilterByBCRespExpenseStatus, setLocalFilterByBCRespExpenseStatus] = useState("");
    const [localFilterByBCCreatedStartDate, setLocalFilterByBCCreatedStartDate] = useState("");
    const [localFilterByBCCreatedEndDate, setLocalFilterByBCCreatedEndDate] = useState("");
    const [isApplyingFilters, setIsApplyingFilters] = useState(false);
    const [showFilters, setShowFilters] = useState(false);

    const { data: ExpenseStatusLookup } = useExpenseStatusLookupQuery('')
    const { data: getAllExpenseType } = useGetAllExpenseTypeQuery('')

    const { data: getUserByManager, refetch: userByManagerRefetch } = useGetAllUsersManagerAdminQuery('')
    const [expenseItemByIdTrigger, { data: expenseItemData, isFetching }] = useLazyGetExpenseItemByExpenseIdQuery({})

    const { data, isFetching: isLoading, refetch, isSuccess } = useGetExpensesReportByUserV2Query({
        reportFilter: 2,
        userAccountId: userAccountID,
        pageNumber: page,
        pageSize: pageSize,
        sortBy: sortBy,
        sortOrder: sortOrder,
        searchStr: '',
        trackingId: searchReporID,
        startDate: filterByStartDate,
        endDate: filterByEndDate,
        dptIds: null,
        userIds: filterByUserIds ? filterByUserIds : null,
        locationIds: null,
        filterByStatusId: filterByStatus,
        filterByHideExpenseForUserManager: localFilterByHideFromManager == "1",
        filterByBCExpenseCreated: filterByBCExpenseCreated === "" ? undefined : filterByBCExpenseCreated === "true",
        bcExpenseCreatedStartDate: filterByBCCreatedStartDate || undefined,
        bcExpenseCreatedEndDate: filterByBCCreatedEndDate || undefined,
        filterByBCRespExpenseStatus: filterByBCRespExpenseStatus || undefined,
    })

    const handlePageSizeChange = (e: any) => {
        const newSize = parseInt(e.target.value);
        setPageSize(newSize);
        setPage(1);
    };

    const handlePageClick = (e: any) => {
        setPage(e.selected + 1);
    };

    const handleSort = (property: string) => {
        setSortBy(property);
        if (sortedColumn === property) {
            setSortOrder(prevSortOrder => (prevSortOrder === "asc" ? "desc" : "asc"));
        } else {
            setSortedColumn(property);
            setSortOrder("asc");
        }
    };

    const handlePageRecords = (e: any) => {
        setPage(1);
    };

    const handleDateRangeChange = (event: any, picker: any) => {
        const startDateFormatted = moment(picker.startDate).format('MM-DD-YYYY');
        const endDateFormatted = moment(picker.endDate).format('MM-DD-YYYY');

        setLocalFilterByStartDate(startDateFormatted);
        setLocalFilterByEndDate(endDateFormatted);
    };

    const handleCancelDateSelection = (event: any, picker: any) => {
        setFilterByStartDate("");
        setFilterByEndDate("");
        setLocalFilterByStartDate("");
        setLocalFilterByEndDate("");
    };

    const handleSearchReportId = (e: any) => {
        const { value } = e.target;
        setLocalSearchReportID(value);
        delayedReportIdSearch(value);
        setPage(1);
    };

    const delayedReportIdSearch = useCallback(
        debounce((value: string) => {
            setSearchReporID(value);
        }, 500),
        []
    );

    const handleFilterByUser = (selectedOption: any) => {
        setLocalFilterByUserIds(selectedOption.value);
    };

    const handleStatusChange = (selectedOption: any) => {
        setLocalFilterByStatus(selectedOption.value);
    };

    const handleLocalFilterByHideFromManagerChange = (selectedOption: any) => {
        setLocalFilterByHideFromManager(selectedOption.value);
    };

    const handleBCExpenseCreatedChange = (selectedOption: any) => {
        setLocalFilterByBCExpenseCreated(selectedOption?.value ?? "");
    };

    const handleBCRespExpenseStatusChange = (selectedOption: any) => {
        setLocalFilterByBCRespExpenseStatus(selectedOption?.value ?? "");
    };

    const handleBCCreatedDateRangeChange = (event: any, picker: any) => {
        setLocalFilterByBCCreatedStartDate(moment(picker.startDate).format('MM-DD-YYYY'));
        setLocalFilterByBCCreatedEndDate(moment(picker.endDate).format('MM-DD-YYYY'));
    };

    const handleBCCreatedDateCancel = () => {
        setLocalFilterByBCCreatedStartDate("");
        setLocalFilterByBCCreatedEndDate("");
        setFilterByBCCreatedStartDate("");
        setFilterByBCCreatedEndDate("");
    };

    const applyFilters = () => {
        setIsApplyingFilters(true);
        setFilterByStartDate(localFilterByStartDate);
        setFilterByEndDate(localFilterByEndDate);
        setFilterByUserIds(localFilterByUserIds)
        setSearchReporID(localSearchReportID);
        setFilterByStatus(localFilterByStatus);
        setLocalFilterByHideFromManager(localFilterByHideFromManager);
        setFilterByBCExpenseCreated(localFilterByBCExpenseCreated);
        setFilterByBCRespExpenseStatus(localFilterByBCRespExpenseStatus);
        setFilterByBCCreatedStartDate(localFilterByBCCreatedStartDate);
        setFilterByBCCreatedEndDate(localFilterByBCCreatedEndDate);
        setPage(1);
    };

    const resetFilters = () => {
        setFilterByStartDate(last30Days);
        setFilterByEndDate(currentDate);
        setFilterByUserIds('')
        setLocalFilterByStartDate(last30Days);
        setLocalFilterByEndDate(currentDate);
        setLocalFilterByUserIds('')
        setLocalSearchReportID('')
        setSearchReporID('')
        setLocalFilterByStatus('0');
        setLocalFilterByHideFromManager('0');
        setFilterByStatus('0')
        setLocalFilterByBCExpenseCreated('');
        setFilterByBCExpenseCreated('');
        setLocalFilterByBCRespExpenseStatus('');
        setFilterByBCRespExpenseStatus('');
        setLocalFilterByBCCreatedStartDate('');
        setLocalFilterByBCCreatedEndDate('');
        setFilterByBCCreatedStartDate('');
        setFilterByBCCreatedEndDate('');
    }

    const debouncedRefetch = debounce(() => {
        refetch().finally(() => {
            setIsApplyingFilters(false);
        });
    }, 300);

    useEffect(() => {
        if (isApplyingFilters) {
            debouncedRefetch();
        }
    }, [isApplyingFilters]);

    useEffect(() => {
        if (sortOrder === "" || sortBy === "") {
            refetch();
        }
    }, [sortOrder, sortBy]);

    const [showExpenseDetailModal, setShowExpenseDetailModal] = useState(false)
    const [expenseDetailData, setExpenseDetailData] = useState({});

    const expenseDetailHandler = () => {
        setShowExpenseDetailModal((prev) => !prev);
    };

    const [impersonateUserModal, setImpersonateUserModal] = useState(false);
    const [impersonateData, setImpersonateData] = useState({});

    const impersonateHandler = () => {
        setImpersonateUserModal((prev) => !prev);
    };

    const [showExpenseLogModal, setShowExpenseLogModal] = useState(false)
    const expenseLogHandler = () => {
        setShowExpenseLogModal((prev) => !prev);
    };

    const [showExpenseDeleteModal, setShowExpenseDeleteModal] = useState(false)
    const [expenseDeleteData, setExpenseDeleteData] = useState({});

    const deleteHandler = () => {
        setShowExpenseDeleteModal((prev) => !prev);
    };

    const handleRefresh = () => {
        setTimeout(() => {
            refetch()
                .then(() => {
                    if (isSuccess) {
                        toast.success(tosterKey.dataRefreshedSuccesfully);
                    }
                });
        }, 0);
    };

    // Get visible columns (excluding actions for the main table body)
    const visibleColumns = COLUMN_CONFIG.filter(col => hiddenColumns.includes(col.id) && col.id !== 'actions');
    const showActions = hiddenColumns.includes('actions');

    return (
        <>
            <ExpenseLogModal show={showExpenseLogModal} handleClose={() => setShowExpenseLogModal(false)} data={expenseDetailData} />
            <ImpersonateUserModal show={impersonateUserModal} handleClose={() => setImpersonateUserModal(false)} data={impersonateData} />
            <ExpenseDetailModal show={showExpenseDetailModal} handleClose={() => setShowExpenseDetailModal(false)} data={expenseDetailData} expenseItems={expenseItemData?.result} isFetching={isFetching} />
            <DeleteExpenseModal
                show={showExpenseDeleteModal}
                handleClose={() => setShowExpenseDeleteModal(false)}
                data={expenseDeleteData}
                refetch={refetch} />
            <PageTitle>{labelKey.searchExpense}</PageTitle>
            <div className='card'>
                {/* begin::Header */}
                <div className='card-header align-items-end border-0 pt-5'>
                    <div className='d-md-flex align-items-center gap-2'>
                        <div>
                            <TextField
                                label=''
                                placeholder='Search Report ID'
                                value={localSearchReportID}
                                onChange={handleSearchReportId}
                            />
                        </div>
                        <div className='w-35px'>
                            <RefreshComponent onClick={handleRefresh} />
                        </div>
                        <div>
                            {!showFilters ? (
                                <button
                                    type="button"
                                    className="btn btn-sm ss-pill-btn ss-pill-btn-filter-active"
                                    onClick={() => setShowFilters(true)}
                                >
                                    <i className="fa fa-filter me-1 text-white" />
                                    Show Filters
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="btn btn-sm ss-pill-btn ss-pill-btn-filter-active"
                                    onClick={() => setShowFilters(false)}
                                >
                                    <i className="fa fa-eye-slash me-1 text-white" />
                                    Hide Filters
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                <div className='card-body py-3'>
                    {showFilters && (
                        <div className="row ss-listing-filters mb-4">
                            <div className="col-md-4 col-lg-2 col-xl-2 mb-3">
                                <SearchSelect
                                    selectClass='filter-valueContainer'
                                    label='User:'
                                    options={[
                                        ...(getUserByManager?.result || []).map((option: any) => ({
                                            value: option.id,
                                            label: option?.value,
                                        })),
                                    ]}
                                    placeholder='Select User'
                                    value={localFilterByUserIds && localFilterByUserIds !== ''
                                        ? {
                                            value: localFilterByUserIds,
                                            label: getUserByManager?.result.find((option: any) => option.id === localFilterByUserIds)?.value
                                        }
                                        : null
                                    }
                                    onChange={handleFilterByUser}
                                />
                            </div>
                            <div className='col-md-4 col-lg-2 col-xl-2 mb-3'>
                                <SearchSelect
                                    selectClass='filter-valueContainer'
                                    label={`${labelKey.statusFilter}:`}
                                    options={[
                                        ...(Array.isArray(ExpenseStatusLookup?.result) ? ExpenseStatusLookup.result.map((option: any) => ({
                                            value: option.id,
                                            label: option?.value,
                                        })) : [])
                                    ]}
                                    placeholder={labelKey.selectStatus}
                                    value={localFilterByStatus && localFilterByStatus !== '0'
                                        ? {
                                            value: localFilterByStatus,
                                            label: ExpenseStatusLookup?.result.find((option: any) => option.id === localFilterByStatus)?.value
                                        }
                                        : null
                                    }
                                    onChange={handleStatusChange}
                                />
                            </div>
                            <div className='col-md-4 col-lg-2 col-xl-2 mb-3'>
                                <SearchSelect
                                    selectClass='filter-valueContainer'
                                    label={`${labelKey.filterByHideExpenseForUserManager}:`}
                                    options={[
                                        { value: "1", label: "Yes" },
                                        { value: "0", label: "No" }
                                    ]}
                                    placeholder={labelKey.filterByHideExpenseForUserManager}
                                    value={localFilterByHideFromManager && localFilterByHideFromManager !== '0'
                                        ? { value: localFilterByHideFromManager, label: "Yes" }
                                        : { value: localFilterByHideFromManager, label: "No" }
                                    }
                                    onChange={handleLocalFilterByHideFromManagerChange}
                                />
                            </div>
                            <div className='col-md-4 col-lg-2 col-xl-2 mb-3'>
                                <DateRangeComp
                                    startDate={localFilterByStartDate || moment().subtract(6, 'days')}
                                    endDate={localFilterByEndDate || moment()}
                                    onApply={handleDateRangeChange}
                                    onCancel={handleCancelDateSelection}
                                    value={localFilterByStartDate && localFilterByEndDate ? `${localFilterByStartDate} - ${localFilterByEndDate}` : "MM/DD/YYYY"}
                                />
                            </div>
                            <div className='col-md-4 col-lg-2 col-xl-2 mb-3'>
                                <SearchSelect
                                    selectClass='filter-valueContainer'
                                    label={`${labelKey.bcExpenseCreatedFilter}:`}
                                    options={[
                                        { value: "true", label: "Yes" },
                                        { value: "false", label: "No" },
                                    ]}
                                    placeholder='Select'
                                    isClearable={true}
                                    value={localFilterByBCExpenseCreated
                                        ? {
                                            value: localFilterByBCExpenseCreated,
                                            label: localFilterByBCExpenseCreated === "true" ? "Yes" : "No",
                                        }
                                        : null}
                                    onChange={handleBCExpenseCreatedChange}
                                />
                            </div>
                            <div className='col-md-4 col-lg-2 col-xl-2 mb-3'>
                                <DateRangeComp
                                    label={labelKey.bcExpenseCreatedDateRange}
                                    startDate={localFilterByBCCreatedStartDate || ''}
                                    endDate={localFilterByBCCreatedEndDate || ''}
                                    onApply={handleBCCreatedDateRangeChange}
                                    onCancel={handleBCCreatedDateCancel}
                                    value={localFilterByBCCreatedStartDate && localFilterByBCCreatedEndDate
                                        ? `${localFilterByBCCreatedStartDate} - ${localFilterByBCCreatedEndDate}`
                                        : "MM/DD/YYYY"}
                                />
                            </div>
                            <div className='col-md-4 col-lg-2 col-xl-2 mb-3'>
                                <SearchSelect
                                    selectClass='filter-valueContainer'
                                    label={`${labelKey.bcRespExpenseStatus}:`}
                                    options={[
                                        { value: "Draft", label: "Draft" },
                                        { value: "Open", label: "Open" },
                                        { value: "Paid", label: "Paid" },
                                    ]}
                                    placeholder='Select Status'
                                    isClearable={true}
                                    value={localFilterByBCRespExpenseStatus
                                        ? {
                                            value: localFilterByBCRespExpenseStatus,
                                            label: localFilterByBCRespExpenseStatus,
                                        }
                                        : null}
                                    onChange={handleBCRespExpenseStatusChange}
                                />
                            </div>
                            <div className='col-md-4 col-lg-2 col-xl-2 mb-3'>
                                <div className='d-flex align-items-end h-100 gap-3 pb-1'>
                                    <button className='btn btn-md btn-danger pull-right' onClick={resetFilters}>Reset Filters</button>
                                    <button className='btn btn-md  btn-primary pull-right' onClick={applyFilters}>
                                        {isApplyingFilters ? (
                                            <>
                                                <span>loading...</span>
                                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                            </>
                                        ) : "Apply Filters"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    <Tooltip id="impersonate" place="left" positionStrategy="fixed" />
                    <Tooltip id="delete-expense" place="left" positionStrategy="fixed" />
                    <Tooltip id="expense-log" place="left" positionStrategy="fixed" />
                    <Tooltip id="expense-detail" place="left" positionStrategy="fixed" />

                    <div className='table-responsive table-height'>
                        <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                            <thead>
                                <tr className='fw-bold text-muted'>
                                    {visibleColumns.map((col) => (
                                        <TableHeading
                                            key={col.id}
                                            label={getLabelText(col)}
                                            columnId={col.id}
                                            sortedColumn={col.sortable ? sortedColumn : undefined}
                                            sortOrder={col.sortable ? sortOrder : undefined}
                                            handleSort={col.sortable ? handleSort : undefined}
                                            className={col.id === 'reportId' ? 'ps-4' : undefined}
                                        />
                                    ))}
                                    {showActions && (
                                        <th className='text-end rounded-end pe-2 border-0 sticky-table-actions'>
                                            <span className='me-2'>{labelKey.actions}</span>
                                            <TableSettingMenu>
                                                {COLUMN_CONFIG.map((col) => (
                                                    <TextField
                                                        key={col.id}
                                                        rightLabel={getLabelText(col)}
                                                        type="checkbox"
                                                        onChange={() => handleToggleColumn(col.id)}
                                                        checked={hiddenColumns.includes(col.id)}
                                                        fieldClass='mb-4'
                                                    />
                                                ))}
                                            </TableSettingMenu>
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {!isLoading && (
                                    <>
                                        {data?.result?.data?.length > 0 ? (
                                            data?.result?.data?.map((dataItem: any, index: any) => (
                                                <tr key={index}>
                                                    {visibleColumns.map((col) => (
                                                        <td key={col.id} className={col.id === 'reportId' ? 'ps-4' : ''}>
                                                            {renderCell(dataItem, col.id, labelKey)}
                                                        </td>
                                                    ))}
                                                    {showActions && (
                                                        <td className='text-end sticky-table-actions' onClick={(e) => e.stopPropagation()}>
                                                            <SsRowActions
                                                                menuId={`expense-search-actions-${dataItem?.expenseId}`}
                                                                appendInline={<DownloadReport data={dataItem} />}
                                                                appendMenu={
                                                                    <div className='ss-row-actions-menu__item px-3'>
                                                                        <DownloadReport data={dataItem} label />
                                                                    </div>
                                                                }
                                                                actions={[
                                                                    ...((roleID === constraintConfig.roleID.role2 && dataItem?.userAccount?.orgUserID) || reportPermision === true
                                                                        ? [{
                                                                            key: 'impersonate',
                                                                            label: labelKey.impersonateUser,
                                                                            tooltipId: 'impersonate',
                                                                            icon: <KTSVG path='/media/icons/duotune/general/impersonate-user.svg' className='svg-icon-3' />,
                                                                            onClick: () => {
                                                                                setImpersonateData(dataItem?.userAccount);
                                                                                impersonateHandler();
                                                                            },
                                                                        }]
                                                                        : []),
                                                                    ...(dataItem?.expenseStatus?.id === constraintConfig.expenseStatus.drafted
                                                                        ? [{
                                                                            key: 'delete',
                                                                            label: 'Delete Expense',
                                                                            tooltipId: 'delete-expense',
                                                                            icon: <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-3 text-danger' svgClassName='svg-danger' />,
                                                                            onClick: () => {
                                                                                setExpenseDeleteData(dataItem);
                                                                                deleteHandler();
                                                                            },
                                                                        }]
                                                                        : []),
                                                                    {
                                                                        key: 'expense-log',
                                                                        label: 'Expense Log',
                                                                        tooltipId: 'expense-log',
                                                                        icon: <FaHistory className='text-dark text-hover-primary' />,
                                                                        onClick: () => {
                                                                            setExpenseDetailData(dataItem);
                                                                            expenseLogHandler();
                                                                        },
                                                                    },
                                                                    {
                                                                        key: 'expense-detail',
                                                                        label: 'Expense Detail',
                                                                        tooltipId: 'expense-detail',
                                                                        icon: <KTSVG path='/media/icons/duotune/arrows/arr064.svg' className='svg-icon-3' />,
                                                                        onClick: () => {
                                                                            expenseItemByIdTrigger({ ExpenseId: dataItem?.expenseId });
                                                                            setExpenseDetailData(dataItem);
                                                                            expenseDetailHandler();
                                                                        },
                                                                    },
                                                                ]}
                                                            />
                                                        </td>
                                                    )}
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={visibleColumns.length + (showActions ? 1 : 0)}>
                                                    <NoRecordFound />
                                                </td>
                                            </tr>
                                        )}
                                    </>
                                )}
                                {isLoading && (
                                    <tr>
                                        <td colSpan={visibleColumns.length + (showActions ? 1 : 0)} className="text-center">
                                            <LoadingComponent />
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <Pagination
                        totalResult={data?.result?.totalRecords === 0 ? 0 : ((data?.result?.pageNumber - 1) * data?.result?.pageSize) + 1}
                        toResult={data?.result?.totalRecords === 0 ? 0 : Math.min(data?.result?.pageNumber * data?.result?.pageSize, data?.result?.totalRecords)}
                        ofResult={data?.result?.totalRecords}
                        onChange={handlePageSizeChange}
                        pageSize={pageSize}
                        onPageChange={handlePageClick}
                        pageCount={data?.result?.totalPages || 0}
                        currentPage={page}
                    />
                </div>
            </div>
        </>
    )
}

export default ExpenseSearch;